<template>
  <div class="productItem-list">
    <!-- 子页面操作栏上方新增父级信息区域 -->
    <el-card class="parent-info-card" shadow="hover">
      <!-- 面包屑导航 -->
      <el-breadcrumb separator-class="el-icon-arrow-right" style="margin-bottom: 10px;">
        <el-breadcrumb-item :to="{ name: 'ProductList' }">套餐组列表</el-breadcrumb-item>
        <el-breadcrumb-item>{{ parentProductName }}</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="product-info">
        <div class="info-row">
          <span class="info-label">关联套餐组：</span>
          <span class="info-value">{{ parentProductName }}</span>
        </div>
      </div>
    </el-card>
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增套餐按钮 -->
        <el-button v-if="hasPermission('product:edit')" icon="el-icon-plus" type="primary" @click="openAddDialog"
          v-hasPermission="'product:edit'">新增套餐</el-button>
        <!-- 返回父页面按钮 -->
        <el-button icon="el-icon-back" @click="$router.back()">
          返回套餐组列表</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="套餐名称">
            <el-input v-model="searchForm.name" placeholder="请输入套餐名称" maxlength="20"></el-input>
          </el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="loadProductItems">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table :data="productItems" border stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center"></el-table-column>
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="name" label="套餐名称" align="center"></el-table-column>
      <el-table-column prop="price" label="售价（单位：元）" align="center" :formatter="formatMoney"></el-table-column>
      <el-table-column prop="bullets" label="包含子弹数（单位：发）" align="center"></el-table-column>
      <el-table-column prop="duration" label="限时（单位：分）" align="center" :formatter="formatDuration"></el-table-column>
      <el-table-column prop="sort" label="显示顺序" align="center"></el-table-column>
      <el-table-column prop="shootMode" label="单发/连发" align="center">
        <template slot-scope="scope">
          <span>{{ scope.row.shootMode === 1 ? '单发' : '连发' }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="status" label="启用状态" align="center">
        <template slot-scope="scope">
          <el-switch v-model="scope.row.status" active-color="#13ce66"
            :active-value="1" :inactive-value="0" :disabled="!hasPermission('product:edit')"
            @change="handleStatusChange(scope.row)"></el-switch>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间"
        :formatter="(row, column, cellValue) => $options.filters.formatTime(cellValue)"
        align="center"></el-table-column>
      <el-table-column label="操作" align="center">
        <template slot-scope="scope">
          <el-tooltip content="编辑" placement="top" v-if="hasPermission('product:edit')">
            <el-button icon="el-icon-edit" type="primary" circle v-hasPermission="'product:edit'"
              @click="openEditDialog(scope.row)"></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
      :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total"></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="250px">
        <el-form-item label="套餐名称" prop="name">
          <el-input v-model="form.name" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="售价（单位：元）(0.01-999.99)" prop="price">
          <el-input-number v-model="form.price" :precision="2" :step="0.01" :min="0.01" :max="999.99"></el-input-number>
        </el-form-item>
        <el-form-item label="包含子弹数（单位：发）（1-999）" prop="bullets">
          <el-input-number v-model="form.bullets" :precision="0" :step="1" :min="1" :max="999"></el-input-number>
        </el-form-item>
        <el-form-item label="限时（单位：分钟）(1-999)" prop="duration">
          <el-input-number v-model="form.duration" :precision="0" :step="1" :min="1" :max="999"></el-input-number>
        </el-form-item>
        <el-form-item label="显示顺序(1-99)" prop="sort">
          <el-input-number v-model="form.sort" :precision="0" :step="1" :min="1" :max="99"></el-input-number>
        </el-form-item>
        <el-form-item label="单发/连发" prop="shootMode">
          <el-radio-group v-model="form.shootMode">
            <el-radio :label="1">单发</el-radio>
            <el-radio :label="2">连发</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import productApi from '../../api/product'
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'ProductItemList',
  data() {
    return {
      // 表格数据
      productItems: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      productId: null, // 父级套餐组ID

      parentProductName: '', // 父级套餐组名称

      // 搜索表单
      searchForm: {
        name: ''
      },

      // 选中的行
      selectedIds: [],

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增套餐',
      isEdit: false,
      form: {
        id: null,
        productId: this.productId,
        name: '',
        price: 10,
        bullets: 10,
        duration: 3,
        sort: 1,
        shootMode: 1,
        status: 0
      },
      rules: {
        name: [
          { required: true, message: '请输入套餐名称', trigger: 'blur' },
          { min: 1, max: 20, message: '套餐名称长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        price: [
          { required: true, message: '请输入售价', trigger: 'change' },
          { type: 'number', min: 0.01, max: 999.99, message: '金额在 0.01 到 999.99 之间', trigger: 'change' }
        ],
        bullets: [
          { required: true, message: '请输入包含子弹数', trigger: 'change' },
          { type: 'number', min: 1, max: 999, message: '子弹发数在 1 到 999 之间', trigger: 'change' }
        ],
        duration: [
          { required: true, message: '请输入限时', trigger: 'change' },
          { type: 'number', min: 1, max: 999, message: '时长在 1 到 999 之间', trigger: 'change' }
        ],
        sort: [
          { required: true, message: '请输入显示顺序', trigger: 'change' },
          { type: 'number', min: 1, max: 99, message: '显示顺序在 1 到 99 之间', trigger: 'change' }
        ],
        shootMode: [
          { required: true, message: '请选择单发/连发', trigger: 'change' },
          { type: 'number', enum: [1, 2], message: '值只能为单发/连发', trigger: 'change' }
        ]
      }
    }
  },

  mounted() {
    this.productId = this.$route.query.productId;
    this.loadParentProductInfo(this.productId); // 加载父级套餐组信息
    this.loadProductItems()
  },

  methods: {
    // 加载套餐列表
    loadProductItems() {
      // 组装请求参数
      const params = {
        name: this.searchForm.name, // 搜索-套餐名称
        productId: this.productId,
        page: this.currentPage,     // 分页-当前页
        size: this.pageSize         // 分页-每页条数
      };
      productApi.findAllProductItem(params).then(response => {
        if (response.success) {
          this.productItems = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载套餐失败：' + error.message)
      })
    },

    // 加载父级套餐组信息（用于面包屑和上下文展示）
    loadParentProductInfo(productId) {
      productApi.findProductById(productId).then(res => {
        if (res.success) {
          this.parentProductName = res.data.name;
        }
      });
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        name: ''
      }
      this.loadProductItems()
    },

    // 每页条数变化时
    handleSizeChange(val) {
      this.pageSize = val;
      this.loadProductItems(); // 重新加载
    },

    // 当前页变化时
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadProductItems(); // 重新加载
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 打开新增对话框
    openAddDialog() {
      if (!hasPermission('product:edit')) {
        this.$message.error('无新增套餐权限');
        return;
      }
      this.dialogTitle = '新增套餐'
      this.isEdit = false
      this.form = {
        id: null,
        productId: this.productId,
        name: '',
        price: 10,
        bullets: 10,
        duration: 3,
        sort: 1,
        shootMode: 1,
        status: 0
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      if (!hasPermission('product:edit')) {
        this.$message.error('无编辑套餐权限');
        return;
      }
      this.dialogTitle = '编辑套餐'
      this.isEdit = true
      this.form = {
        ...row,
        price: row.price / 100,
        duration: row.duration / 60
      }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          let submitData = { ...this.form };
          // 金额：元 转 分
          submitData.price = Math.round(submitData.price * 100);
          // 时长：分钟 转 秒
          submitData.duration = Math.round(submitData.duration * 60);
          if (this.isEdit) {
            // 更新套餐
            productApi.updateProductItem(this.form.id, submitData).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadProductItems()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增套餐
            productApi.addProductItem(submitData).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadProductItems()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 修改套餐状态
    handleStatusChange(row) {
      if (!hasPermission('product:edit')) {
        this.$message.error('无操作套餐权限');
        return;
      }
      this.$confirm(`确定要${row.status === 1 ? '启用' : '禁用'}该套餐吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        productApi.productItemStatus(row.id, row.status).then(response => {
          if (response.success) {
            this.$message.success(response.message || '操作成功')
            this.loadProductItems()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          row.status = row.status === 1 ? 0 : 1;
          this.$message.error('操作失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
        row.status = row.status === 1 ? 0 : 1;
      });
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },

    formatMoney(row){
      return (row.price / 100).toFixed(2)
    },

    formatDuration(row){
      return (row.duration / 60).toFixed(0)
    }
  }
}
</script>

<style scoped>
.parent-info-card {
  margin-bottom: 20px;
  border-left: 3px solid #409EFF;
  /* 保持标识性边框 */
}

.product-info {
  margin: 10px 0;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.info-label {
  width: 120px;
  /* 固定标签宽度，确保对齐 */
  text-align: right;
  margin-right: 10px;
}
</style>